import useApiStatus from "@kamae-apps/shared/Hooks/useApiStatus/useApiStatus"
import { UserConfigStateProvider } from "@kamae-apps/shared/Hooks/userConfig/Context"
import { CompanyStatus } from "@kamae-apps/shared/Types/Company/CompanyStatus"
import { Scope, scopeHas } from "@kamae-apps/shared/Types/Scope"
import { ToastContainer } from "@kamae-apps/shared/old/Component/Toast/Toast"
import React, { FC, MutableRefObject, Suspense } from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"
import { AppLayout } from "./Components/Layout/AppLayout"
import AppLoader from "./Components/Organisms/Loaders/AppLoader/AppLoader"
import PageLoader from "./Components/Organisms/Loaders/PageLoader/PageLoader"
import { useUser } from "./Contexts/User/UserContext"
import Error404Page from "./Routes/Error404/Error404Page"
import AppLayoutGuard from "./Routes/Guards/AppLayoutGuard"
import UserGuard from "./Routes/Guards/UserGuard"
import LearningPage from "./Routes/Learning/LearningPage"
import MaintenancePage from "./Routes/Maintenance/MaintenancePage"
import { companyService } from "./Services/CompanyService"

const TrainingsPage = React.lazy(() => import("./Routes/Trainings/TrainingsPage"))
const LoginPage = React.lazy(() => import("./Routes/Login/LoginPage"))
const OrganizationPage = React.lazy(() => import("./Routes/Organization/OrganizationPage"))
const OrganizationUsersPage = React.lazy(() => import("./Routes/Organization/Users/UsersPage"))
const OrganizationTeamsPage = React.lazy(() => import("./Routes/Organization/Teams/TeamsPage"))
const OrganizationToolsPage = React.lazy(() => import("./Routes/Organization/Tools/ToolsPage"))
const WelcomePage = React.lazy(() => import("./Routes/Welcome/WelcomePage"))
const AvatarsPage = React.lazy(() => import("./Routes/Avatars/AvatarsPage"))
const HomePage = React.lazy(() => import("./Routes/Home/HomePage"))
const HomeLegacy = React.lazy(() => import("./Routes/HomeLegacy/HomePage"))
const PhishingCreatePage = React.lazy(() => import("./Routes/Phishing/Create/PhishingCreatePage"))
const LibraryPage = React.lazy(() => import("./Routes/Library/LibraryPage"))
const SupervisionPage = React.lazy(() => import("./Routes/Supervision/SupervisionPage"))
const AutomationPage = React.lazy(() => import("./Routes/Phishing/Automation/AutomationPage"))
const PhishingPageRouter = React.lazy(() =>
  import("./Routes/Phishing/PhishingRouter").then(module => ({ default: module.PhishingPageRouter }))
)
const PhishingDetailPageRouter = React.lazy(() =>
  import("./Routes/Phishing/PhishingRouter").then(module => ({ default: module.PhishingDetailPageRouter }))
)
const PhishingAdminCampaignResultPage = React.lazy(
  () => import("./Routes/Phishing/Admin/PhishingAdminCampaignResultPage/PhishingAdminCampaignResultPage")
)
const BotPage = React.lazy(() => import("./Routes/Bot/BotPage"))
const ChallengePageLegacy = React.lazy(() => import("./Routes/Challenge/legacy/ChallengePageLegacy"))
const ChallengePage = React.lazy(() => import("./Routes/Challenge/ChallengePage"))
const DashboardPage = React.lazy(() => import("./Routes/Dashboard/DashboardPage"))
const TatamiPage = React.lazy(() => import("./Routes/Tatami/TatamiPage"))
const DojoPage = React.lazy(() => import("./Routes/Dojo/DojoPage"))

type AppProps = {
  loaded: boolean
  toastRef: MutableRefObject<ToastContainer>
  elearningReload: boolean
  getUser: () => Promise<void>
  doReload: () => void
  featureFlag: string[]
  displayNewBeltModal: boolean
  setDisplayNewBeltModal: React.Dispatch<React.SetStateAction<boolean>>
}

export const RebrandedApp: FC<AppProps> = ({
  loaded,
  toastRef,
  doReload,
  elearningReload,
  featureFlag,
  getUser,
  displayNewBeltModal,
  setDisplayNewBeltModal,
}) => {
  const apiStatus = useApiStatus()
  const { t } = useTranslation("app")
  const { user } = useUser()

  const displayRankings = companyService.displayRankings()

  const intercom = document.querySelector(".intercom-lightweight-app-launcher.intercom-launcher")

  if (intercom) {
    // Workaround before we modify the intercom component inside the app settings //
    intercom.setAttribute("style", "background: #056EF7")
  }

  return (
    <div className={"bg-gradient-ocean bg-no-repeat bg-fixed overflow-hidden"}>
      {((apiStatus.code === 503 || apiStatus.code === -1) && <MaintenancePage />) || (
        <Routes>
          <Route
            path={"/login"}
            element={
              <Suspense fallback={<AppLoader />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            element={
              <UserGuard loaded={loaded}>
                <UserConfigStateProvider loaded={loaded}>
                  <AppLayoutGuard>
                    <Outlet />
                  </AppLayoutGuard>
                </UserConfigStateProvider>
              </UserGuard>
            }
          >
            <>
              {typeof user.avatar === "undefined" && (
                <Route
                  path={"/welcome"}
                  element={
                    <Suspense fallback={<AppLoader />}>
                      <WelcomePage />
                    </Suspense>
                  }
                />
              )}
              {featureFlag.includes("new-challenge") && (
                <Route
                  path={"/new-challenge"}
                  element={
                    <Suspense fallback={<AppLoader />}>
                      <ChallengePage />
                    </Suspense>
                  }
                />
              )}
            </>
          </Route>
          <Route
            element={
              <UserGuard loaded={loaded}>
                <UserConfigStateProvider loaded={loaded}>
                  <AppLayoutGuard>
                    <AppLayout
                      t={t}
                      loaded={loaded}
                      toastRef={toastRef}
                      displayNewBeltModal={displayNewBeltModal}
                      setDisplayNewBeltModal={setDisplayNewBeltModal}
                    >
                      <Outlet />
                    </AppLayout>
                  </AppLayoutGuard>
                </UserConfigStateProvider>
              </UserGuard>
            }
          >
            {scopeHas(user?.scope, Scope.COMPANY_ADMIN) && (
              <>
                <Route path={"/organization"}>
                  <Route
                    path={""}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <OrganizationPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path={"users"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <OrganizationUsersPage company={user.company} />
                      </Suspense>
                    }
                  />
                  <Route
                    path={"teams"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <OrganizationTeamsPage
                          company={user.company}
                          getUser={getUser}
                        />
                      </Suspense>
                    }
                  />
                  <Route
                    path={"tools"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <OrganizationToolsPage />
                      </Suspense>
                    }
                  />
                </Route>
                {user.company.status === CompanyStatus.Starter && (
                  <Route
                    path={"/supervision"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <SupervisionPage />
                      </Suspense>
                    }
                  />
                )}
              </>
            )}
            {user.company.status !== CompanyStatus.Starter &&
              (scopeHas(user.scope, Scope.COMPANY_ADMIN) ||
                (user.id === user.team.leader?.id && featureFlag.includes("reporting-team-leader"))) && (
                <Route
                  path={"/dashboard"}
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <DashboardPage />
                    </Suspense>
                  }
                />
              )}
            <Route
              path={"/home"}
              element={
                <Suspense fallback={<PageLoader />}>
                  {featureFlag.includes("new-home-page") ? (
                    <HomePage />
                  ) : (
                    <HomeLegacy elearningReload={elearningReload} />
                  )}
                </Suspense>
              }
            />
            <Route
              path={"/elearning"}
              element={
                featureFlag.includes("new-challenge") ? (
                  <Navigate
                    to="/tatami#trainings"
                    replace
                  />
                ) : (
                  <Suspense fallback={<PageLoader />}>
                    <TrainingsPage
                      doReload={doReload}
                      reload={elearningReload}
                    />
                  </Suspense>
                )
              }
            />
            {featureFlag.includes("new-challenge") && (
              <Route
                path={"/tatami"}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TatamiPage />
                  </Suspense>
                }
              />
            )}
            {featureFlag.includes("seasonal-ranking") && displayRankings && (
              <Route
                path={"/dojo"}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DojoPage />
                  </Suspense>
                }
              />
            )}
            {user.company.phishing_enabled && (
              <Route path={"/phishing"}>
                <Route
                  index
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PhishingPageRouter />
                    </Suspense>
                  }
                />
                <Route
                  path={":id"}
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PhishingDetailPageRouter />
                    </Suspense>
                  }
                />
                {featureFlag.includes("new-phishing-administration-modale") && (
                  <Route
                    path={":campaignId/user/:userId"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <PhishingAdminCampaignResultPage />
                      </Suspense>
                    }
                  />
                )}
                {scopeHas(user?.scope, Scope.COMPANY_ADMIN) &&
                  !featureFlag.includes("phishing-attack-creation-disabled") && (
                    <Route
                      path={"create"}
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PhishingCreatePage />
                        </Suspense>
                      }
                    />
                  )}
                {scopeHas(user?.scope, Scope.COMPANY_ADMIN) &&
                  user.company.status !== CompanyStatus.Starter &&
                  featureFlag.includes("phishing-automated") && (
                    <Route
                      path={"automation"}
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AutomationPage />
                        </Suspense>
                      }
                    />
                  )}
              </Route>
            )}
            <Route
              path={"/reflexes"}
              element={
                <Suspense fallback={<PageLoader />}>
                  <LibraryPage />
                </Suspense>
              }
            />
            <Route
              path={"/profile"}
              element={
                <Suspense fallback={<PageLoader />}>
                  <AvatarsPage />
                </Suspense>
              }
            />
            {user.company.status === CompanyStatus.Starter && (
              <Route
                path={"/challenge"}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ChallengePageLegacy />
                  </Suspense>
                }
              />
            )}
            <Route
              path={"*"}
              element={<Error404Page />}
            />
          </Route>
          <Route
            path={"/bot"}
            element={<BotPage />}
          />
          <Route
            path={"/learning-page"}
            element={<LearningPage />}
          />
        </Routes>
      )}
    </div>
  )
}
